// aifab - Common components
import { useState } from 'react';

// ── Spinner ───────────────────────────────────────────────────────────────────
export function Spinner({ size = 20 }) {
  return (
    <div style={{
      width: size, height: size, flexShrink: 0,
      border: '2px solid var(--bd2)',
      borderTopColor: 'var(--or)',
      borderRadius: '50%',
      animation: 'spin .65s linear infinite',
      display: 'inline-block',
    }} />
  );
}

// ── StatusBadge ───────────────────────────────────────────────────────────────
const STATUS_MAP = {
  COMPLETED:             'b-green',
  ONLINE:                'b-green',
  PROFILED:              'b-green',
  ACTIVE:                'b-green',
  RUNNING:               'b-blue',
  BUSY:                  'b-or',
  LOADING:               'b-or',
  QUEUED:                'b-gray',
  CREATED:               'b-gray',
  DRAFT:                 'b-gray',
  PENDING:               'b-gray',
  UNKNOWN:               'b-gray',
  CANCELLED:             'b-gray',
  WAITING_FOR_RESOURCES: 'b-amber',
  OFFLINE:               'b-red',
  FAILED:                'b-red',
  FAILED_DATA:           'b-red',
  TIMEOUT:               'b-red',
  ERROR:                 'b-red',
  NOT_VALIDATED:         'b-gray',
  PASSED:                'b-green',
  PAUSED:                'b-amber',
  ARCHIVED:              'b-gray',
};

const STATUS_DOT = {
  COMPLETED: 'dot-green', ONLINE: 'dot-green', PROFILED: 'dot-green', ACTIVE: 'dot-green',
  RUNNING: 'dot-blue', BUSY: 'dot-blue', LOADING: 'dot-blue',
  OFFLINE: 'dot-red', FAILED: 'dot-red', FAILED_DATA: 'dot-red',
  WAITING_FOR_RESOURCES: 'dot-or',
};

export function StatusBadge({ code }) {
  if (!code) return null;
  const cls = STATUS_MAP[code] || 'b-gray';
  const dot = STATUS_DOT[code];
  return (
    <span className={`badge ${cls}`} style={{ gap: 5 }}>
      {dot && <span className={`dot ${dot}`} style={{ width: 5, height: 5 }} />}
      {code.replace(/_/g, ' ')}
    </span>
  );
}

// ── StatusDot (inline, no label) ──────────────────────────────────────────────
export function StatusDot({ code }) {
  const cls = STATUS_DOT[code] || 'dot-gray';
  return <span className={`dot ${cls}`} title={code} />;
}

// ── Modal ─────────────────────────────────────────────────────────────────────
export function Modal({ title, subtitle, onClose, children, wide, xl }) {
  const sizeClass = xl ? 'modal-xl' : wide ? 'modal-wide' : '';
  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className={`modal ${sizeClass}`}>
        <div className="modal-hd">
          <div>
            <div className="modal-title">{title}</div>
            {subtitle && <div className="modal-sub">{subtitle}</div>}
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ── ModalBody / ModalFooter ───────────────────────────────────────────────────
export function ModalBody({ children, style }) {
  return <div className="modal-body" style={style}>{children}</div>;
}
export function ModalFooter({ children }) {
  return <div className="modal-ft">{children}</div>;
}

// ── Alert / Err ───────────────────────────────────────────────────────────────
export function Alert({ type = 'info', children }) {
  const cls = { err: 'alert-err', ok: 'alert-ok', warn: 'alert-warn', info: 'alert-info' }[type] || 'alert-info';
  return <div className={`alert ${cls}`}>{children}</div>;
}
export function Err({ msg }) {
  const [expanded, setExpanded] = useState(false);
  if (!msg) return null;
  const text = String(msg);
  const isLong = text.length > 220;
  if (!isLong) return <div className="alert alert-err">{text}</div>;
  return (
    <div className="alert alert-err" style={{ maxWidth: '100%', overflow: 'hidden' }}>
      <div>{text.slice(0, 200)}…</div>
      <button onClick={() => setExpanded(v => !v)} style={{
        background: 'none', border: 'none', padding: 0, marginTop: 4,
        color: 'inherit', textDecoration: 'underline', cursor: 'pointer',
        fontSize: 11, fontFamily: 'inherit',
      }}>
        {expanded ? 'Hide details' : 'Show details'}
      </button>
      {expanded && (
        <pre style={{
          marginTop: 6, maxHeight: 180, overflow: 'auto',
          whiteSpace: 'pre-wrap', wordBreak: 'break-word',
          fontSize: 10.5, fontFamily: 'var(--mono)', lineHeight: 1.45,
          background: 'rgba(0,0,0,.04)', borderRadius: 6, padding: '6px 8px',
        }}>{text}</pre>
      )}
    </div>
  );
}

// ── EmptyState ────────────────────────────────────────────────────────────────
export function EmptyState({ icon, title, subtitle, action }) {
  return (
    <div className="empty">
      {icon && <div className="empty-icon">{icon}</div>}
      <div className="empty-title">{title}</div>
      {subtitle && <div className="empty-sub c-3">{subtitle}</div>}
      {action && <div style={{ marginTop: 16 }}>{action}</div>}
    </div>
  );
}

// ── SectionCard ───────────────────────────────────────────────────────────────
export function SectionCard({ title, action, children, bodyPad = true }) {
  return (
    <div className="card mb16">
      {(title || action) && (
        <div className="card-hd">
          {title && <div className="card-title">{title}</div>}
          {action}
        </div>
      )}
      <div className={bodyPad ? 'card-body' : ''}>{children}</div>
    </div>
  );
}

// ── Step indicator ────────────────────────────────────────────────────────────
export function Steps({ steps, current }) {
  return (
    <div className="flex gap0 mb20" style={{ borderBottom: '1px solid var(--bd)' }}>
      {steps.map((label, i) => (
        <div key={i} style={{
          flex: 1, padding: '8px 12px', textAlign: 'center',
          fontSize: 10, fontWeight: 700, letterSpacing: '.07em',
          textTransform: 'uppercase',
          borderBottom: `2px solid ${current === i ? 'var(--or)' : 'transparent'}`,
          color: current === i ? 'var(--or)' : current > i ? '#334155' : '#64748B',
          marginBottom: -1,
        }}>
          <span style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 16, height: 16, borderRadius: '50%', marginRight: 6,
            background: current === i ? 'var(--or)' : current > i ? 'var(--green)' : '#C8D5E8',
            color: current >= i ? '#fff' : '#64748B', fontSize: 9, fontWeight: 700,
          }}>{current > i ? '✓' : i + 1}</span>
          {label}
        </div>
      ))}
    </div>
  );
}

// ── Confirm dialog ────────────────────────────────────────────────────────────
export function Confirm({ title, message, onConfirm, onCancel, danger }) {
  return (
    <div className="overlay">
      <div className="modal" style={{ width: 400 }}>
        <div className="modal-hd">
          <div className="modal-title">{title}</div>
          <button className="modal-close" onClick={onCancel}>✕</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.6 }}>{message}</p>
        </div>
        <div className="modal-ft">
          <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
          <button className={`btn btn-sm ${danger ? 'btn-danger' : 'btn-primary'}`} onClick={onConfirm}>
            {title}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── CopyButton ────────────────────────────────────────────────────────────────
export function CopyButton({ text, size = 'sm' }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <button className={`btn btn-ghost btn-${size}`} onClick={copy}>
      {copied ? '✓' : '⎘'}
    </button>
  );
}

// ── Hint (inline learning-assistant help) ──────────────────────────────────────
// A small "?" affordance next to any label/value that explains what it means
// on hover or focus. This is the platform's inline-help layer: instead of a
// separate docs page, short explanations sit right next to the thing they
// explain. Keyboard accessible (real <button>, shown on focus not just hover).
export function Hint({ text, children, position = 'top' }) {
  const [show, setShow] = useState(false);
  const posStyle = position === 'bottom'
    ? { top: '100%', marginTop: 6 }
    : { bottom: '100%', marginBottom: 6 };
  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
      {children}
      <button
        type="button"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onFocus={() => setShow(true)}
        onBlur={() => setShow(false)}
        onClick={e => { e.stopPropagation(); setShow(v => !v); }}
        style={{
          width: 14, height: 14, marginLeft: 4, flexShrink: 0,
          borderRadius: '50%', border: '1px solid var(--bd2)',
          background: 'var(--surface-alt, #F1F5F9)', color: 'var(--text-muted, #64748B)',
          fontSize: 9, fontWeight: 700, lineHeight: '12px',
          cursor: 'help', padding: 0,
        }}
        aria-label={text}
      >?</button>
      {show && (
        <div style={{
          position: 'absolute', left: 0, ...posStyle,
          zIndex: 50, width: 230, padding: '8px 10px',
          background: '#1A1714', color: '#fff', fontSize: 11, lineHeight: 1.5,
          borderRadius: 6, boxShadow: '0 4px 14px rgba(0,0,0,.18)',
          fontWeight: 400, whiteSpace: 'normal',
          textTransform: 'none', letterSpacing: 'normal',
        }}>
          {text}
        </div>
      )}
    </span>
  );
}

